import React, {useCallback, useState} from 'react';
import {Box, Divider, InputAdornment, Stack, Tooltip, Typography} from '@mui/material';
import {FunctionsOutlined} from '@mui/icons-material';
import {alpha, useTheme} from '@mui/material/styles';
import {useTranslation} from 'react-i18next';
import {formatMoney} from '../../../../../../core/services/utils/utils';
import Button from '../../../../base/button/button';

const FormulaDrivenPriceAdornment = ({
                                         formula,
                                         resolvedValue,
                                         circularError = null,
                                         onEditFormula,
                                         editDisabled = false,
                                     }) => {
    const theme = useTheme();
    const {t} = useTranslation('components', {keyPrefix: 'app-specific.formula-input.formula-driven-price-adornment'});
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const isError = !!circularError;

    const accent = isError ? theme.palette.error : theme.palette.primary;

    const chipSx = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 28,
        height: 26,
        borderRadius: '10px',
        border: '1px solid',
        borderColor: alpha(accent.main, isError ? 0.45 : 0.28),
        backgroundColor: alpha(accent.main, isError ? 0.1 : 0.07),
        color: accent.dark ?? accent.main,
        cursor: 'help',
        userSelect: 'none',
        transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow'], {
            duration: theme.transitions.duration.shorter,
        }),
        '&:hover': {
            backgroundColor: alpha(accent.main, isError ? 0.16 : 0.12),
            borderColor: alpha(accent.main, isError ? 0.55 : 0.4),
            boxShadow: `0 0 0 3px ${alpha(accent.main, 0.14)}`,
        },
    };

    const handleEditClick = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setTooltipOpen(false);
        onEditFormula?.();
    }, [onEditFormula]);

    const tooltipTitle = (
        <Box sx={{py: 1.5, px: 2, maxWidth: 380}} onMouseDown={(e) => e.stopPropagation()}>
            <Stack spacing={1.25}>
                <Box>
                    <Typography variant="caption" fontWeight={700} sx={{textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.85}}>
                        {t('title')}
                    </Typography>
                    <Typography variant="body2" sx={{mt: 0.25, opacity: 0.8}}>
                        {t('description')}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        px: 1.25,
                        py: 1,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: alpha(theme.palette.common.white, 0.12),
                        backgroundColor: alpha(theme.palette.common.black, 0.2),
                    }}
                >
                    <Typography
                        variant="body2"
                        component="code"
                        sx={{
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                            fontSize: '0.8125rem',
                            fontWeight: 500,
                            lineHeight: 1.45,
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-wrap',
                        }}
                    >
                        {formula}
                    </Typography>
                </Box>
                {
                    resolvedValue != null && !Number.isNaN(resolvedValue) &&
                    <>
                        <Divider sx={{opacity: 0.2}}/>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                            <Typography variant="caption" sx={{opacity: 0.75}}>
                                {t('resolvedValueLabel')}
                            </Typography>
                            <Box
                                sx={{
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 1,
                                    backgroundColor: alpha(theme.palette.success.main, 0.22),
                                    color: theme.palette.success.light,
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                }}
                            >
                                {formatMoney(resolvedValue)}
                            </Box>
                        </Stack>
                    </>
                }
                {
                    circularError &&
                    <>
                        <Divider sx={{opacity: 0.2}}/>
                        <Typography variant="caption" color="error.light" fontWeight={600} lineHeight={1.4}>
                            {circularError}
                        </Typography>
                    </>
                }
                {
                    onEditFormula &&
                    <>
                        <Divider sx={{opacity: 0.2}}/>
                        <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            fullWidth
                            disabled={editDisabled}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={handleEditClick}
                        >
                            {t('editFormula')}
                        </Button>
                    </>
                }
            </Stack>
        </Box>
    );

    return (
        <InputAdornment position="end" sx={{ml: 0.75, mr: 0.25, height: 'auto', maxHeight: 'none'}}>
            <Tooltip
                title={tooltipTitle}
                placement="top"
                arrow
                open={tooltipOpen}
                onOpen={() => setTooltipOpen(true)}
                onClose={() => setTooltipOpen(false)}
                disableInteractive={false}
                leaveDelay={200}
                slotProps={{
                    tooltip: {
                        sx: {
                            p: 0,
                            maxWidth: 400,
                        },
                    },
                    popper: {
                        sx: {zIndex: 1300},
                    },
                }}
            >
                <Box component="span" sx={chipSx} aria-label={t('ariaLabel')}>
                    <FunctionsOutlined sx={{fontSize: 16, opacity: 0.92}}/>
                </Box>
            </Tooltip>
        </InputAdornment>
    );
};

export default FormulaDrivenPriceAdornment;
